import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Modal from '../components/shared/Modal';
import SearchableDropdown from '../components/shared/SearchableDropdown';
import { useToast, ToastContainer } from '../../../shared/components/ui/Toast';
import { getInternalNodeOptions, getInternalNodeOptionsByScope } from '../constants/internalNodeOptions';
import { statTypesService, type StatTypeOption } from '../services/statTypesService';
import {
  listMappings,
  createMapping,
  updateMapping,
  deleteMapping,
  type OrganisationDataMapping,
  type EntityType,
} from '../../../shared/services/clientDataMappingService';
import {
  organizationsService,
  getOrganisationApiConfig,
  upsertOrganisationApiConfig,
  type Organization,
  type OrganisationApiConfig,
} from '../services';

const ENTITY_TYPES: { value: EntityType; label: string }[] = [
  { value: 'match', label: 'Match details' },
  { value: 'match_stats', label: 'Match stats' },
  { value: 'player', label: 'Player stats' },
  { value: 'stat', label: 'Stat name' },
  { value: 'highlight', label: 'Highlight' },
  { value: 'tactical_chart', label: 'Tactical chart' },
  { value: 'match_list', label: 'Match list' },
  { value: 'create_match', label: 'Create match' },
];

type ApiSectionKey = 'match_details' | 'match_stats' | 'player_stats' | 'tactical_charts' | 'highlights';
type ApiNodeKey =
  | 'match_details_nodes'
  | 'match_stats_nodes'
  | 'player_stats_nodes'
  | 'tactical_charts_nodes'
  | 'highlights_nodes'
  | 'match_list_nodes'
  | 'create_match_nodes';

type ApiSectionConfig = {
  toggleKey?: ApiSectionKey;
  label: string;
  description: string;
  nodesKey: ApiNodeKey;
  nodesLabel: string;
  nodesDescription: string;
  entityType: EntityType;
};

const API_SECTION_CONFIGS: ApiSectionConfig[] = [
  {
    toggleKey: 'match_details',
    label: 'Match details',
    description: 'Include match info, teams, lineup, officials, score summary, key events in Get Statistics response.',
    nodesKey: 'match_details_nodes',
    nodesLabel: 'Match details nodes',
    nodesDescription: 'Choose specific match detail fields to include. Leave empty to include all.',
    entityType: 'match',
  },
  {
    toggleKey: 'match_stats',
    label: 'Match stats',
    description: 'Include team-level statistics (home_team_stats, away_team_stats) in Get Statistics response.',
    nodesKey: 'match_stats_nodes',
    nodesLabel: 'Match stats nodes',
    nodesDescription: 'Choose specific team statistics fields to include. Leave empty to include all.',
    entityType: 'match_stats',
  },
  {
    toggleKey: 'player_stats',
    label: 'Player stats',
    description: 'Include per-player statistics in Get Statistics response.',
    nodesKey: 'player_stats_nodes',
    nodesLabel: 'Player stats nodes',
    nodesDescription: 'Choose specific per-player fields (including stats.* fields) to include. Leave empty to include all.',
    entityType: 'player',
  },
  {
    toggleKey: 'tactical_charts',
    label: 'Tactical charts',
    description: 'Include tactical chart URLs in Get Statistics response.',
    nodesKey: 'tactical_charts_nodes',
    nodesLabel: 'Tactical chart nodes',
    nodesDescription: 'Choose specific tactical chart fields to include. Leave empty to include all.',
    entityType: 'tactical_chart',
  },
  {
    toggleKey: 'highlights',
    label: 'Highlights',
    description: 'Include highlight video URLs in Get Statistics response.',
    nodesKey: 'highlights_nodes',
    nodesLabel: 'Highlight nodes',
    nodesDescription: 'Choose specific highlight fields to include. Leave empty to include all.',
    entityType: 'highlight',
  },
  {
    label: 'Match list',
    description: 'Configure the list matches response fields for this organisation.',
    nodesKey: 'match_list_nodes',
    nodesLabel: 'Match list nodes',
    nodesDescription: 'Choose specific match list fields to include. Leave empty to include all.',
    entityType: 'match_list',
  },
  {
    label: 'Create match response',
    description: 'Configure the create match response fields for this organisation.',
    nodesKey: 'create_match_nodes',
    nodesLabel: 'Create match response nodes',
    nodesDescription: 'Choose specific create match response fields to include. Leave empty to include all.',
    entityType: 'create_match',
  },
];

interface ApiConfigForm {
  match_details: boolean;
  match_stats: boolean;
  player_stats: boolean;
  tactical_charts: boolean;
  highlights: boolean;
  match_details_nodes: string[];
  match_stats_nodes: string[];
  player_stats_nodes: string[];
  tactical_charts_nodes: string[];
  highlights_nodes: string[];
  match_list_nodes: string[];
  create_match_nodes: string[];
  video_detection_field_paths: string[];
  match_stats_stat_keys: string[];
  player_stats_stat_keys: string[];
}

const DEFAULT_API_CONFIG: ApiConfigForm = {
  match_details: true,
  match_stats: true,
  player_stats: true,
  tactical_charts: true,
  highlights: true,
  match_details_nodes: [],
  match_stats_nodes: [],
  player_stats_nodes: [],
  tactical_charts_nodes: [],
  highlights_nodes: [],
  match_list_nodes: [],
  create_match_nodes: [],
  video_detection_field_paths: [],
  match_stats_stat_keys: [],
  player_stats_stat_keys: [],
};

interface ClientIntegrationsPageProps {
  orgId: string; // current org from URL (e.g. super admin's context); used as default for "configure for" selector
}

interface CombinedMapping {
  key: string;
  entity_type: EntityType;
  client_field: string;
  internal_field: string;
  input_id?: string;
  output_id?: string;
}

type MappingScope = 'request' | 'response' | 'both';

const ClientIntegrationsPage: React.FC<ClientIntegrationsPageProps> = ({ orgId: currentOrgId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isApiVisibility = location.pathname.includes('api-visibility');

  const [organisations, setOrganisations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [mappings, setMappings] = useState<OrganisationDataMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<CombinedMapping | null>(null);
  const [formSource, setFormSource] = useState(''); // client field
  const [formTarget, setFormTarget] = useState(''); // internal field
  const [formEntityType, setFormEntityType] = useState<EntityType>('match');
  const [formScope, setFormScope] = useState<MappingScope>('both');
  const { toasts, showToast, removeToast } = useToast();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<CombinedMapping | null>(null);
  const [statTypeOptions, setStatTypeOptions] = useState<StatTypeOption[]>([]);
  const [statTypeLoading, setStatTypeLoading] = useState(false);
  const [statTypeError, setStatTypeError] = useState<string | null>(null);

  // API visibility (organisation_api_config)
  const [apiConfig, setApiConfig] = useState<ApiConfigForm>(DEFAULT_API_CONFIG);
  const [apiConfigRow, setApiConfigRow] = useState<OrganisationApiConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [nodeSearch, setNodeSearch] = useState<Record<ApiNodeKey, string>>({
    match_details_nodes: '',
    match_stats_nodes: '',
    player_stats_nodes: '',
    tactical_charts_nodes: '',
    highlights_nodes: '',
    match_list_nodes: '',
    create_match_nodes: '',
  });
  const [videoDetectionPaths, setVideoDetectionPaths] = useState<string>('');
  const [drawerSection, setDrawerSection] = useState<ApiSectionConfig | null>(null);
  const [drawerStatKeys, setDrawerStatKeys] = useState(false);
  const [useSameStatKeysForPlayer, setUseSameStatKeysForPlayer] = useState(false);
  const [statKeysSearch, setStatKeysSearch] = useState({ match_stats: '', player_stats: '' });

  // Load organisations once (super admin can configure for any client org)
  useEffect(() => {
    let cancelled = false;
    organizationsService.getAll().then((list) => {
      if (!cancelled) {
        setOrganisations(list);
        // Don't auto-select an organization - user must explicitly select one
      }
    });
    return () => { cancelled = true; };
  }, [currentOrgId]);

  const loadMappings = async () => {
    if (!selectedOrgId) return;
    setLoading(true);
    const data = await listMappings(selectedOrgId);
    setMappings(data);
    setLoading(false);
  };

  useEffect(() => {
    loadMappings();
  }, [selectedOrgId]);

  const loadStatTypes = async () => {
    setStatTypeLoading(true);
    setStatTypeError(null);
    try {
      const rows = await statTypesService.list();
      setStatTypeOptions(rows);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load stat types';
      setStatTypeError(message);
    } finally {
      setStatTypeLoading(false);
    }
  };

  const loadApiConfig = async () => {
    if (!selectedOrgId) return;
    setConfigLoading(true);
    try {
      const row = await getOrganisationApiConfig(selectedOrgId);
      if (row) {
        setApiConfigRow(row);
        const matchKeys = row.match_stats_stat_keys || [];
        const playerKeys = row.player_stats_stat_keys || [];
        const useSame = matchKeys.length > 0 && JSON.stringify([...matchKeys].sort()) === JSON.stringify([...playerKeys].sort());
        
        setApiConfig({
          match_details: row.match_details ?? true,
          match_stats: row.match_stats ?? true,
          player_stats: row.player_stats ?? true,
          tactical_charts: row.tactical_charts ?? true,
          highlights: row.highlights ?? true,
          match_details_nodes: row.match_details_nodes ?? [],
          match_stats_nodes: row.match_stats_nodes ?? [],
          player_stats_nodes: row.player_stats_nodes ?? [],
          tactical_charts_nodes: row.tactical_charts_nodes ?? [],
          highlights_nodes: row.highlights_nodes ?? [],
          match_list_nodes: row.match_list_nodes ?? [],
          create_match_nodes: row.create_match_nodes ?? [],
          video_detection_field_paths: row.video_detection_field_paths ?? [],
          match_stats_stat_keys: matchKeys,
          player_stats_stat_keys: playerKeys,
        });
        setUseSameStatKeysForPlayer(useSame);
        setVideoDetectionPaths((row.video_detection_field_paths ?? []).join('\n'));
      } else {
        setApiConfigRow(null);
        setApiConfig(DEFAULT_API_CONFIG);
        setUseSameStatKeysForPlayer(false);
        setVideoDetectionPaths('');
      }
    } catch {
      setApiConfigRow(null);
      setApiConfig(DEFAULT_API_CONFIG);
    } finally {
      setConfigLoading(false);
    }
  };

  // Redirect /client-integrations to /client-integrations/data-mappings
  useEffect(() => {
    if (location.pathname.endsWith('client-integrations')) {
      navigate(location.pathname + '/data-mappings', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (selectedOrgId) loadApiConfig();
  }, [selectedOrgId]);

  useEffect(() => {
    if (isModalOpen && formEntityType === 'stat' && statTypeOptions.length === 0 && !statTypeLoading) {
      loadStatTypes();
    }
  }, [isModalOpen, formEntityType]);

  // Load stat types when API visibility tab is active and org is selected
  useEffect(() => {
    if (isApiVisibility && selectedOrgId && statTypeOptions.length === 0 && !statTypeLoading) {
      loadStatTypes();
    }
  }, [isApiVisibility, selectedOrgId]);

  const handleSaveApiConfig = async () => {
    if (!selectedOrgId) return;
    setConfigSaving(true);
    const videoPathsArray = videoDetectionPaths
      .split('\n')
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    const { data, error } = await upsertOrganisationApiConfig(selectedOrgId, {
      match_details: apiConfig.match_details,
      match_stats: apiConfig.match_stats,
      player_stats: apiConfig.player_stats,
      tactical_charts: apiConfig.tactical_charts,
      highlights: apiConfig.highlights,
      match_details_nodes: apiConfig.match_details_nodes.length ? apiConfig.match_details_nodes : null,
      match_stats_nodes: apiConfig.match_stats_nodes.length ? apiConfig.match_stats_nodes : null,
      player_stats_nodes: apiConfig.player_stats_nodes.length ? apiConfig.player_stats_nodes : null,
      tactical_charts_nodes: apiConfig.tactical_charts_nodes.length ? apiConfig.tactical_charts_nodes : null,
      highlights_nodes: apiConfig.highlights_nodes.length ? apiConfig.highlights_nodes : null,
      match_list_nodes: apiConfig.match_list_nodes.length ? apiConfig.match_list_nodes : null,
      create_match_nodes: apiConfig.create_match_nodes.length ? apiConfig.create_match_nodes : null,
      video_detection_field_paths: videoPathsArray.length > 0 ? videoPathsArray : null,
      match_stats_stat_keys: apiConfig.match_stats_stat_keys.length > 0 ? apiConfig.match_stats_stat_keys : null,
      player_stats_stat_keys: useSameStatKeysForPlayer
        ? (apiConfig.match_stats_stat_keys.length > 0 ? apiConfig.match_stats_stat_keys : null)
        : (apiConfig.player_stats_stat_keys.length > 0 ? apiConfig.player_stats_stat_keys : null),
    });
    setConfigSaving(false);
    if (error) {
      showToast(error, 'error');
      return;
    }
    if (data) {
      setApiConfigRow(data);
      setApiConfig((prev) => ({ ...prev, video_detection_field_paths: videoPathsArray }));
    }
    showToast('API visibility saved.', 'success');
  };

  const combinedMappings: CombinedMapping[] = (() => {
    const map = new Map<string, CombinedMapping>();
    for (const m of mappings) {
      const isInput = m.mapping_type === 'input';
      const clientField = isInput ? m.source_field : m.target_field;
      const internalField = isInput ? m.target_field : m.source_field;
      const key = `${m.entity_type}||${clientField}||${internalField}`;
      const existing = map.get(key);
      const next: CombinedMapping = existing || {
        key,
        entity_type: m.entity_type as EntityType,
        client_field: clientField,
        internal_field: internalField,
      };
      if (isInput) next.input_id = m.id;
      else next.output_id = m.id;
      map.set(key, next);
    }
    return Array.from(map.values());
  })();

  const toggleNode = (nodesKey: ApiNodeKey, node: string) => {
    setApiConfig((prev) => {
      const current = new Set(prev[nodesKey]);
      if (current.has(node)) current.delete(node);
      else current.add(node);
      return { ...prev, [nodesKey]: Array.from(current) };
    });
  };

  const handleMatchStatsStatKeysChange = (keys: string[]) => {
    setApiConfig((prev) => ({ ...prev, match_stats_stat_keys: keys }));
    if (useSameStatKeysForPlayer) {
      setApiConfig((prev) => ({ ...prev, player_stats_stat_keys: keys }));
    }
  };

  const handleUseSameStatKeysChange = (checked: boolean) => {
    setUseSameStatKeysForPlayer(checked);
    if (checked) {
      setApiConfig((prev) => ({ ...prev, player_stats_stat_keys: [...prev.match_stats_stat_keys] }));
    }
  };

  const renderStatKeysSection = () => {
    const matchStatsEnabled = apiConfig.match_stats;
    const playerStatsEnabled = apiConfig.player_stats;
    const matchStatsSelectedKeys = apiConfig.match_stats_stat_keys;
    const playerStatsSelectedKeys = apiConfig.player_stats_stat_keys;
    const matchStatsSearchTerm = statKeysSearch.match_stats || '';
    const playerStatsSearchTerm = statKeysSearch.player_stats || '';
    
    const filteredMatchStatsTypes = statTypeOptions.filter((st) =>
      st.stat_key.toLowerCase().includes(matchStatsSearchTerm.toLowerCase()) ||
      (st.display_name && st.display_name.toLowerCase().includes(matchStatsSearchTerm.toLowerCase()))
    );
    
    const filteredPlayerStatsTypes = statTypeOptions.filter((st) =>
      st.stat_key.toLowerCase().includes(playerStatsSearchTerm.toLowerCase()) ||
      (st.display_name && st.display_name.toLowerCase().includes(playerStatsSearchTerm.toLowerCase()))
    );

    return (
      <div className="border-t border-gray-200 px-4 py-4 bg-gray-50">
        {statTypeLoading && (
          <div className="text-sm text-gray-500 mb-3">Loading stat types...</div>
        )}
        {!statTypeLoading && statTypeOptions.length === 0 && (
          <div className="text-sm text-gray-500 mb-3">No stat types available. Please refresh the page.</div>
        )}

        {/* Checkbox to use same selection for both */}
        <div className="mb-4 pb-4 border-b border-gray-300">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="use-same-stat-keys"
              checked={useSameStatKeysForPlayer}
              onChange={(e) => handleUseSameStatKeysChange(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              disabled={!matchStatsEnabled || !playerStatsEnabled}
            />
            <label htmlFor="use-same-stat-keys" className="ml-2 text-sm font-medium text-gray-700">
              Use same stat keys for match stats and player stats
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            When checked, player stats will use the same stat keys selection as match stats.
          </p>
        </div>

        {/* Match Stats Allow List */}
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Match Stats Allow List</h3>
              <p className="text-xs text-gray-600 mt-1">
                Select which stat keys to include in match stats. Leave empty to include all stats.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Selected: {matchStatsSelectedKeys.length || 'All'} {matchStatsSelectedKeys.length ? 'stat keys' : 'stat keys (default)'}
              </p>
            </div>
            {statTypeOptions.length > 0 && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:text-blue-700"
                  onClick={() => {
                    if (!matchStatsEnabled) return;
                    handleMatchStatsStatKeysChange(statTypeOptions.map((st) => st.stat_key));
                  }}
                  disabled={!matchStatsEnabled}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:text-blue-700"
                  onClick={() => {
                    if (!matchStatsEnabled) return;
                    handleMatchStatsStatKeysChange([]);
                  }}
                  disabled={!matchStatsEnabled}
                >
                  Clear selection
                </button>
              </div>
            )}
          </div>

          {statTypeOptions.length > 0 && (
            <>
              <div className="mt-3">
                <input
                  type="text"
                  value={matchStatsSearchTerm}
                  onChange={(e) => setStatKeysSearch((prev) => ({ ...prev, match_stats: e.target.value }))}
                  placeholder="Filter stat keys..."
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  disabled={!matchStatsEnabled}
                />
              </div>
              <div className="mt-3 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white">
                {filteredMatchStatsTypes.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-500">No stat keys match the filter.</div>
                ) : (
                  filteredMatchStatsTypes.map((statType) => (
                    <label
                      key={statType.stat_key}
                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={matchStatsSelectedKeys.includes(statType.stat_key)}
                        onChange={() => {
                          const newKeys = matchStatsSelectedKeys.includes(statType.stat_key)
                            ? matchStatsSelectedKeys.filter((k) => k !== statType.stat_key)
                            : [...matchStatsSelectedKeys, statType.stat_key];
                          handleMatchStatsStatKeysChange(newKeys);
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        disabled={!matchStatsEnabled}
                      />
                      <span className={matchStatsEnabled ? 'text-gray-800' : 'text-gray-400'}>
                        {statType.display_name || statType.stat_key} <span className="text-gray-500">({statType.stat_key})</span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {/* Player Stats Allow List */}
        <div>
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Player Stats Allow List</h3>
              <p className="text-xs text-gray-600 mt-1">
                Select which stat keys to include in player stats. Leave empty to include all stats.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {useSameStatKeysForPlayer ? (
                  <span className="italic">Using match stats stat keys: {matchStatsSelectedKeys.length || 'All'}</span>
                ) : (
                  <>Selected: {playerStatsSelectedKeys.length || 'All'} {playerStatsSelectedKeys.length ? 'stat keys' : 'stat keys (default)'}</>
                )}
              </p>
            </div>
            {statTypeOptions.length > 0 && !useSameStatKeysForPlayer && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:text-blue-700"
                  onClick={() => {
                    if (!playerStatsEnabled) return;
                    setApiConfig((prev) => ({ ...prev, player_stats_stat_keys: statTypeOptions.map((st) => st.stat_key) }));
                  }}
                  disabled={!playerStatsEnabled}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:text-blue-700"
                  onClick={() => {
                    if (!playerStatsEnabled) return;
                    setApiConfig((prev) => ({ ...prev, player_stats_stat_keys: [] }));
                  }}
                  disabled={!playerStatsEnabled}
                >
                  Clear selection
                </button>
              </div>
            )}
          </div>

          {useSameStatKeysForPlayer ? (
            <div className="text-sm text-gray-500 italic bg-blue-50 px-3 py-2 rounded-md">
              Player stats will use the same stat keys as match stats above.
            </div>
          ) : statTypeOptions.length > 0 ? (
            <>
              <div className="mt-3">
                <input
                  type="text"
                  value={playerStatsSearchTerm}
                  onChange={(e) => setStatKeysSearch((prev) => ({ ...prev, player_stats: e.target.value }))}
                  placeholder="Filter stat keys..."
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  disabled={!playerStatsEnabled}
                />
              </div>
              <div className="mt-3 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white">
                {filteredPlayerStatsTypes.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-500">No stat keys match the filter.</div>
                ) : (
                  filteredPlayerStatsTypes.map((statType) => (
                    <label
                      key={statType.stat_key}
                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={playerStatsSelectedKeys.includes(statType.stat_key)}
                        onChange={() => {
                          const newKeys = playerStatsSelectedKeys.includes(statType.stat_key)
                            ? playerStatsSelectedKeys.filter((k) => k !== statType.stat_key)
                            : [...playerStatsSelectedKeys, statType.stat_key];
                          setApiConfig((prev) => ({ ...prev, player_stats_stat_keys: newKeys }));
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        disabled={!playerStatsEnabled}
                      />
                      <span className={playerStatsEnabled ? 'text-gray-800' : 'text-gray-400'}>
                        {statType.display_name || statType.stat_key} <span className="text-gray-500">({statType.stat_key})</span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    );
  };

  const renderNodeControls = (section: ApiSectionConfig, isDrawer: boolean) => {
    const nodes = getInternalNodeOptions(section.entityType);
    const searchTerm = nodeSearch[section.nodesKey] || '';
    const filteredNodes = nodes.filter((node) => node.toLowerCase().includes(searchTerm.toLowerCase()));
    const isSectionEnabled = section.toggleKey ? apiConfig[section.toggleKey] : true;
    const selectedCount = apiConfig[section.nodesKey].length;
    return (
      <div className={isDrawer ? '' : 'border-t border-gray-200 px-4 py-4'}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{section.nodesLabel}</h3>
            <p className="text-xs text-gray-600 mt-1">{section.nodesDescription}</p>
            <p className="text-xs text-gray-500 mt-1">
              Selected: {selectedCount || 'All'} {selectedCount ? 'nodes' : 'nodes (default)'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="text-xs text-blue-600 hover:text-blue-700"
              onClick={() => {
                if (!isSectionEnabled) return;
                setApiConfig((prev) => ({ ...prev, [section.nodesKey]: nodes }));
              }}
              disabled={!isSectionEnabled}
            >
              Select all
            </button>
            <button
              type="button"
              className="text-xs text-blue-600 hover:text-blue-700"
              onClick={() => {
                if (!isSectionEnabled) return;
                setApiConfig((prev) => ({ ...prev, [section.nodesKey]: [] }));
              }}
              disabled={!isSectionEnabled}
            >
              Clear selection
            </button>
          </div>
        </div>
        <div className="mt-3">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setNodeSearch((prev) => ({ ...prev, [section.nodesKey]: e.target.value }))}
            placeholder="Filter nodes..."
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            disabled={!isSectionEnabled}
          />
        </div>
        <div className="mt-3 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white">
          {filteredNodes.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500">No nodes match the filter.</div>
          ) : (
            filteredNodes.map((node) => (
              <label key={node} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={apiConfig[section.nodesKey].includes(node)}
                  onChange={() => toggleNode(section.nodesKey, node)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={!isSectionEnabled}
                />
                <span className={isSectionEnabled ? 'text-gray-800' : 'text-gray-400'}>{node}</span>
              </label>
            ))
          )}
        </div>
      </div>
    );
  };

  const openAdd = () => {
    setEditingMapping(null);
    setFormSource('');
    setFormTarget('');
    setFormEntityType('match');
    setFormScope('response');
    setIsModalOpen(true);
  };

  const openEdit = (m: CombinedMapping) => {
    setEditingMapping(m);
    setFormSource(m.client_field);
    setFormTarget(m.internal_field);
    setFormEntityType(m.entity_type);
    setFormScope(
      m.entity_type === 'create_match'
        ? m.input_id && m.output_id
          ? 'both'
          : m.input_id
            ? 'request'
            : 'response'
        : 'response'
    );
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formSource.trim() || !formTarget.trim()) {
      showToast('Client and internal fields are required', 'error');
      return;
    }
    const clientField = formSource.trim();
    const internalField = formTarget.trim();
    const wantsInput = formScope === 'request' || formScope === 'both';
    const wantsOutput = formScope === 'response' || formScope === 'both';
    if (editingMapping) {
      if (wantsInput) {
        const inputResult = editingMapping.input_id
          ? await updateMapping(editingMapping.input_id, {
              source_field: clientField,
              target_field: internalField,
            })
          : await createMapping(selectedOrgId, 'input', formEntityType, clientField, internalField);
        if (inputResult.error) {
          showToast(inputResult.error, 'error');
          return;
        }
      } else if (editingMapping.input_id) {
        const { error } = await deleteMapping(editingMapping.input_id);
        if (error) {
          showToast(error, 'error');
          return;
        }
      }

      if (wantsOutput) {
        const outputResult = editingMapping.output_id
          ? await updateMapping(editingMapping.output_id, {
              source_field: internalField,
              target_field: clientField,
            })
          : await createMapping(selectedOrgId, 'output', formEntityType, internalField, clientField);
        if (outputResult.error) {
          showToast(outputResult.error, 'error');
          return;
        }
      } else if (editingMapping.output_id) {
        const { error } = await deleteMapping(editingMapping.output_id);
        if (error) {
          showToast(error, 'error');
          return;
        }
      }
      showToast('Mapping updated', 'success');
    } else {
      if (wantsInput) {
        const inputResult = await createMapping(selectedOrgId, 'input', formEntityType, clientField, internalField);
        if (inputResult.error) {
          showToast(inputResult.error, 'error');
          return;
        }
      }
      if (wantsOutput) {
        const outputResult = await createMapping(selectedOrgId, 'output', formEntityType, internalField, clientField);
        if (outputResult.error) {
          showToast(outputResult.error, 'error');
          return;
        }
      }
      showToast('Mapping added', 'success');
    }
    setIsModalOpen(false);
    loadMappings();
  };

  const getVisibilityNodesForEntity = (entityType: EntityType): string[] | null => {
    switch (entityType) {
      case 'match':
        return apiConfig.match_details_nodes.length ? apiConfig.match_details_nodes : null;
      case 'match_stats':
        return apiConfig.match_stats_nodes.length ? apiConfig.match_stats_nodes : null;
      case 'player':
        return apiConfig.player_stats_nodes.length ? apiConfig.player_stats_nodes : null;
      case 'tactical_chart':
        return apiConfig.tactical_charts_nodes.length ? apiConfig.tactical_charts_nodes : null;
      case 'highlight':
        return apiConfig.highlights_nodes.length ? apiConfig.highlights_nodes : null;
      case 'match_list':
        return apiConfig.match_list_nodes.length ? apiConfig.match_list_nodes : null;
      case 'create_match':
        return apiConfig.create_match_nodes.length ? apiConfig.create_match_nodes : null;
      default:
        return null;
    }
  };

  const openDelete = (mapping: CombinedMapping) => {
    setPendingDelete(mapping);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    const errors: string[] = [];
    if (pendingDelete.input_id) {
      const { error } = await deleteMapping(pendingDelete.input_id);
      if (error) errors.push(error);
    }
    if (pendingDelete.output_id) {
      const { error } = await deleteMapping(pendingDelete.output_id);
      if (error) errors.push(error);
    }
    if (errors.length) {
      showToast(errors[0], 'error');
      return;
    }
    showToast('Mapping deleted', 'success');
    loadMappings();
    setIsDeleteModalOpen(false);
    setPendingDelete(null);
  };

  const selectedOrg = organisations.find((o) => o.id === selectedOrgId);

  return (
    <div className="flex-1 flex flex-col p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isApiVisibility ? 'API visibility' : 'Data Mappings'}
          </h1>
          <p className="text-gray-600">
            {isApiVisibility
              ? 'Control which sections (match details, stats, player stats, charts, highlights) this organisation receives in the Get Statistics API response.'
              : 'Data mapping for client API integration (field names and structure). These mappings are used by the APIs when responding to that organisation.'}
          </p>
        </div>
        {!isApiVisibility && (
          <button
            onClick={openAdd}
            disabled={!selectedOrgId}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>+ Add mapping</span>
          </button>
        )}
      </div>

      {/* Organisation selector */}
      <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="max-w-md">
          <SearchableDropdown
            label="Configure for"
            value={selectedOrgId}
            onChange={(value) => setSelectedOrgId(value)}
            placeholder="Select an organisation…"
            options={organisations.map((o) => ({
              id: o.id,
              name: o.name,
              subtitle: o.subdomain ? o.subdomain : undefined,
            }))}
          />
        </div>
        {selectedOrg && (
          <p className="mt-2 text-sm text-gray-500">
            {isApiVisibility
              ? `API visibility below applies to Get Statistics responses for ${selectedOrg.name}.`
              : `Mappings below apply to API responses for ${selectedOrg.name}. If a node is not mapped here, the API returns the original internal name.`}
          </p>
        )}
      </div>

      {!isApiVisibility && (
        <div className="mb-4 text-sm text-gray-600">
          Mappings can apply to request bodies, API responses, or both.
        </div>
      )}

      {isApiVisibility ? (
        /* API visibility tab content */
        !selectedOrgId ? (
          <div className="p-8 text-center text-gray-500 bg-gray-50 border border-gray-200 rounded-lg">
            Select organisation to see details
          </div>
        ) : configLoading ? (
          <p className="text-gray-500">Loading API visibility…</p>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-6 w-full">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">API visibility (default for this org)</h2>
            <p className="text-sm text-gray-600 mb-6">
              Configure which fields are included in client API responses. If no config is set, all fields are included.
            </p>
            {apiConfigRow && (
              <div className="mb-5 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                <div className="flex flex-wrap gap-x-6 gap-y-1">
                  <span><strong>Config ID:</strong> {apiConfigRow.id}</span>
                  <span><strong>Status:</strong> {apiConfigRow.is_active ? 'active' : 'inactive'}</span>
                  <span><strong>Updated:</strong> {new Date(apiConfigRow.updated_at).toLocaleString()}</span>
                </div>
              </div>
            )}
            <div className="space-y-4">
              {API_SECTION_CONFIGS.map((section) => (
                <details key={section.nodesKey} className="group border border-gray-200 rounded-lg bg-white">
                  <summary className="flex items-start justify-between gap-4 cursor-pointer list-none px-4 py-3">
                    <div className="flex items-start gap-4">
                      {section.toggleKey ? (
                        <div className="flex items-center h-6">
                          <input
                            id={`api-config-${section.toggleKey}`}
                            type="checkbox"
                            checked={apiConfig[section.toggleKey]}
                            onChange={(e) => setApiConfig((prev) => ({ ...prev, [section.toggleKey!]: e.target.checked }))}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center h-6">
                          <input
                            type="checkbox"
                            disabled
                            checked
                            className="h-4 w-4 rounded border-gray-300 accent-blue-600 opacity-70 cursor-not-allowed"
                            aria-label="Always enabled"
                          />
                        </div>
                      )}
                      <label htmlFor={section.toggleKey ? `api-config-${section.toggleKey}` : undefined} className="flex-1 cursor-pointer">
                        <span className="font-medium text-gray-900">{section.label}</span>
                        <p className="text-sm text-gray-600 mt-0.5">{section.description}</p>
                      </label>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDrawerSection(section);
                        }}
                        title="Open drawer"
                        aria-label="Open drawer"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </button>
                      <span className="text-gray-400 group-open:rotate-180 transition-transform">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </span>
                    </div>
                  </summary>
                  {renderNodeControls(section, false)}
                </details>
              ))}
              
              {/* Stats Keys Section - Separate top-level section */}
              <details className="group border border-gray-200 rounded-lg bg-white">
                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none px-4 py-3">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center h-6">
                      <input
                        type="checkbox"
                        disabled
                        checked
                        className="h-4 w-4 rounded border-gray-300 accent-blue-600 opacity-70 cursor-not-allowed"
                        aria-label="Always enabled"
                      />
                    </div>
                    <label className="flex-1 cursor-pointer">
                      <span className="font-medium text-gray-900">Stats Keys</span>
                      <p className="text-sm text-gray-600 mt-0.5">
                        Configure which stat keys are included in match stats and player stats arrays.
                      </p>
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDrawerStatKeys(true);
                      }}
                      title="Open drawer"
                      aria-label="Open drawer"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </button>
                    <span className="text-gray-400 group-open:rotate-180 transition-transform">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                  </div>
                </summary>
                {renderStatKeysSection()}
              </details>
            </div>
            
            {/* Video Detection Field Paths */}
            <div className="mt-6 border-t border-gray-200 pt-6">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Video Detection Field Paths</h3>
              <p className="text-sm text-gray-600 mb-3">
                Configure JSON path patterns for detecting video URLs in CMS API tab submission payloads. One path per line.
                Examples: <code className="bg-gray-100 px-1 rounded">matchVideo</code>, <code className="bg-gray-100 px-1 rounded">videoUrl</code>, <code className="bg-gray-100 px-1 rounded">videos[0].url</code>
              </p>
              <textarea
                value={videoDetectionPaths}
                onChange={(e) => setVideoDetectionPaths(e.target.value)}
                placeholder="matchVideo&#10;videoUrl&#10;videos[0].url"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                If empty, system uses default fallback patterns. Paths are evaluated in order, first match wins.
              </p>
            </div>
            
            <div className="mt-6">
              <button
                onClick={handleSaveApiConfig}
                disabled={configSaving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {configSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )
      ) : !selectedOrgId ? (
        <div className="p-8 text-center text-gray-500 bg-gray-50 border border-gray-200 rounded-lg">
          Select organisation to see details
        </div>
      ) : loading ? (
        <p className="text-gray-500">Loading mappings…</p>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Client field</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Internal field</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Entity type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Scope</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {combinedMappings.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                    No mappings yet. Add one to map client format to internal (and vice versa).
                  </td>
                </tr>
              ) : (
                combinedMappings.map((m) => (
                  <tr key={m.key} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-900">{m.client_field}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{m.internal_field}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{m.entity_type}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {m.input_id && m.output_id ? 'Request + response' : m.input_id ? 'Request body' : 'API response'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => openEdit(m)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          aria-label="Edit mapping"
                          title="Edit mapping"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => openDelete(m)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          aria-label="Delete mapping"
                          title="Delete mapping"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {drawerSection && (
        <div className="fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setDrawerSection(null)}
            role="button"
            aria-label="Close drawer"
          />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-base font-semibold text-gray-900">{drawerSection.nodesLabel}</h2>
                <p className="text-xs text-gray-600 mt-1">{drawerSection.nodesDescription}</p>
              </div>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setDrawerSection(null)}
                aria-label="Close drawer"
                title="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto">
              {renderNodeControls(drawerSection, true)}
            </div>
          </div>
        </div>
      )}

      {drawerStatKeys && (
        <div className="fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setDrawerStatKeys(false)}
            role="button"
            aria-label="Close drawer"
          />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Stats Keys</h2>
                <p className="text-xs text-gray-600 mt-1">
                  Configure which stat keys are included in match stats and player stats arrays.
                </p>
              </div>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600 transition-colors"
                onClick={() => setDrawerStatKeys(false)}
                aria-label="Close drawer"
                title="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {renderStatKeysSection()}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setIsModalOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">{editingMapping ? 'Edit mapping' : 'Add mapping'}</h2>
            <div className="space-y-3">
              {!editingMapping && (
                <SearchableDropdown
                  label="Entity type"
                  value={formEntityType}
                  onChange={(value) => {
                    const nextType = value as EntityType;
                    setFormEntityType(nextType);
                    setFormScope(nextType === 'create_match' ? formScope : 'response');
                    setFormTarget('');
                  }}
                  placeholder="Select an entity type…"
                  options={ENTITY_TYPES.map(({ value, label }) => ({ id: value, name: label }))}
                />
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700">Mapping scope</label>
                <select
                  value={formScope}
                  onChange={(e) => setFormScope(e.target.value as MappingScope)}
                  disabled={formEntityType !== 'create_match'}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                >
                  {formEntityType === 'create_match' ? (
                    <>
                      <option value="request">Request body</option>
                      <option value="response">API response</option>
                      <option value="both">Both</option>
                    </>
                  ) : (
                    <option value="response">API response (fixed)</option>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Client field</label>
                <input
                  type="text"
                  value={formSource}
                  onChange={(e) => setFormSource(e.target.value)}
                  placeholder="e.g. teamA or playerInfo.name"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                />
              </div>
              <div>
                {formEntityType === 'stat' ? (
                  <>
                    <SearchableDropdown
                      label="Internal field (stat key)"
                      value={formTarget}
                      onChange={(value) => setFormTarget(value)}
                      placeholder="Select a stat key…"
                      options={statTypeOptions.map((s) => ({
                        id: s.stat_key,
                        name: s.stat_key,
                        subtitle: `${s.is_calculated ? 'calculated · ' : ''}${s.display_name || s.short_description || ''}`.trim() || undefined,
                      }))}
                      disabled={statTypeLoading}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Stats are loaded from <code>stat_types</code>. Calculated stats are marked.
                    </p>
                    {statTypeLoading && (
                      <p className="mt-1 text-xs text-gray-500">Loading stat types…</p>
                    )}
                    {statTypeError && (
                      <p className="mt-1 text-xs text-red-600">{statTypeError}</p>
                    )}
                  </>
                ) : (
                  <>
                    <SearchableDropdown
                      label="Internal field"
                      value={formTarget}
                      onChange={(value) => setFormTarget(value)}
                      placeholder="Select or type an internal field…"
                      options={(() => {
                        const base = getInternalNodeOptionsByScope(formEntityType, formScope);
                        const visibilityNodes = formScope === 'response' ? getVisibilityNodesForEntity(formEntityType) : null;
                        const filtered = visibilityNodes ? base.filter((node) => visibilityNodes.includes(node)) : base;
                        return filtered.map((node) => ({ id: node, name: node }));
                      })()}
                      allowCustom
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Suggestions are based on known internal fields for this entity. You can still type a custom path.
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-2">
              <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save</button>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setPendingDelete(null);
        }}
        title="Delete mapping"
        className="max-w-md"
      >
        <p className="text-sm text-gray-700">
          This will remove the mapping in both directions (client → internal and internal → client).
          This action cannot be undone.
        </p>
        <div className="mt-6 flex justify-end space-x-2">
          <button
            onClick={() => {
              setIsDeleteModalOpen(false);
              setPendingDelete(null);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </Modal>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default ClientIntegrationsPage;
